import type { MarketId, TemplateView } from '@ps/web-sports-api-types';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import type { RootState } from '@swf/core/store';
import { Title } from '@swf/sports/components';
import useEventStatus from '@swf/sports/components/EventStatus/hooks/useEventStatus';
import { ExpandablePanel } from '@swf/sports/components/ExpandablePanel/ExpandablePanel';
import FormattedDateTime from '@swf/sports/components/FormattedDateTime/FormattedDateTime';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import { isValidInPlayStatus } from '@swf/sports/utils/isValidInPlayStatus';

// TODO GSFES-1234 refactor to use composition or shared.scss
// eslint-disable-next-line no-restricted-imports
import styles from '../../GroupedTemplatePanels.scss';
import CompetitionLink from '../CompetitionsLink/CompetitionsLink';
import GroupInPanel from '../GroupInPanel/GroupInPanel';

import { TXTWCL_N0_logo } from './CompoundMarketTemplatePanel.messages.json';

interface Props {
  competitionGroup: TemplateView[][];
  marketIds: (MarketId | null)[];
  groupedOn: 'DATE' | 'COMPETITION';
  isNested: boolean;
  flush?: boolean;
}

const CompoundMarketTemplatePanel: React.FC<Props> = ({
  competitionGroup,
  groupedOn,
  marketIds,
  isNested,
  flush: isFlush = false
}) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();

  const marketId = marketIds.find(Boolean) ?? '';
  const market = useSelector((state: RootState) => widgetSelect.market(state, marketId));
  const event = useSelector((state: RootState) =>
    widgetSelect.event(state, market ? market.eventId : -1)
  );
  const competition = useSelector((state: RootState) =>
    widgetSelect.competition(state, event.competitionId)
  );
  const panelName = groupedOn === 'DATE' ? event.eventStartTime : competition.competitionName;
  const { logoUrl } = competition;
  const shouldShowLogoUrl = !!logoUrl && groupedOn === 'COMPETITION';

  const eventStatus = useEventStatus({
    inPlay: market?.inPlay,
    startTime: event.eventStartTime,
    canTurnInPlay: market?.offeredInPlay
  });

  const { formatMessage } = useRegulatoryIntl();

  return (
    <div
      className={classNames(styles.panel, {
        [styles.nestedPanel]: isNested,
        [styles.flush]: isFlush,
        [styles.inPlay]: isValidInPlayStatus(eventStatus)
      })}
      key={panelName}
    >
      <ExpandablePanel
        openInitially
        isNested={isNested}
        title={
          <Title
            isNested={isNested}
            isLight
            hasIcon={shouldShowLogoUrl}
            hasLink={groupedOn === 'COMPETITION' && isNested}
          >
            {shouldShowLogoUrl ? (
              <img
                className={styles.logoImg}
                src={logoUrl}
                alt={formatMessage(TXTWCL_N0_logo, { competitionName: panelName })}
              />
            ) : null}
            {groupedOn === 'DATE' ? (
              <FormattedDateTime date={panelName} format="long" weekday="long" hideTime />
            ) : isNested ? (
              <CompetitionLink competitionId={competition.competitionId} />
            ) : (
              panelName
            )}
          </Title>
        }
      >
        {competitionGroup.map((groupInPanel) => (
          <GroupInPanel key={`${marketIds.join(',')}_panel}`} groupInPanel={groupInPanel} />
        ))}
      </ExpandablePanel>
    </div>
  );
};

export default CompoundMarketTemplatePanel;
